import classNames from 'classnames';
import { FormikConfig, useFormikContext } from 'formik';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InfoContent } from '../../components/InfoContent';
import { CreateModal } from '../../components/Modal/CreateModal';
import { FormikCheckBox } from '../../components/formik/FormCheckBox';
import { FormikDayInput } from '../../components/formik/FormDayInput';
import { FormInput, FormikInput } from '../../components/formik/FormInput';
import { FormikSelect, FormSelectProps } from '../../components/formik/FormSelect';
import { Option } from '../../components/inputs/Select';
import { useSubscriptionTypeTranslations } from '../../hooks/translations/useSubscriptionTypeTranslations';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useFormatDate } from '../../hooks/useFormatDate';
import { usePlanOptions } from '../../hooks/usePlanOptions';
import { ToastType, useToastNotifications } from '../../hooks/useToastNotifications';
import { T, useT } from '../../translation/src';
import {
  InvoiceVia,
  PlanChangeKind,
  PlanChangeProblem,
  SubscriptionType,
  SuperadminPlanChangePreviewQuery,
  SuperadminPlanChangePreviewQueryVariables,
  useSuperadminPlanChangePreviewQuery,
  useSuperadminUpdatePlanMutation,
} from '../../types/graphqlTypes';
import { dayAfterTermEnd, toDateInput } from '../../utils/dates';
import { formatPrice } from '../../utils/formatPrice';
import { NoBillingDetailsWarning } from './NoBillingDetailsWarning';
import { PlanCalendar } from './PlanCalendar';

type Preview = SuperadminPlanChangePreviewQuery['superadminPlanChangePreview'];
type TermState = Preview['current'];

interface FormValues {
  subscriptionType: SubscriptionType;
  // CUSTOM only: the deal's values. Euros; the API speaks cents.
  monthlyCredits: number | '';
  projectUnitPrice: number | '';
  subscriptionPrice: number | '';
  // The day the change takes effect (today or later). Empty means "the server's
  // default", which the simulation fills in. A new term runs a year from it; an
  // upgrade keeps the term's renewal date (the billing date has a window of its own).
  effectiveAt: string;
  // Bill `price` (euros) instead of the computed amount.
  overridePrice: boolean;
  price: number | '';
  // Void the current term now: the customer did not pay for it.
  force: boolean;
}

const useTranslations = () => {
  const monthlyCredits = useT('monthly projects', { swc: true });
  const projectUnitPrice = useT('price per project (€)', { swc: true });
  const subscriptionPrice = useT('yearly price (€)', { swc: true });
  const plan = useT('plan', { swc: true });
  const effectiveAt = useT('effective date', { swc: true });
  const overridePrice = useT('override the price', { swc: true });
  const force = useT('apply now and void the current term (the customer did not pay for it)', { swc: true });
  const price = useT('price (€)', { swc: true });
  const changePlan = useT('change plan', { swc: true });
  const confirm = useT('confirm', { swc: true });
  const payAsYouGo = useT('pay as you go');
  const failed = useT('could not change the plan', { swc: true });
  return { monthlyCredits, projectUnitPrice, subscriptionPrice, plan, effectiveAt, overridePrice, force, price, changePlan, confirm, payAsYouGo, failed };
};

const toCents = (euros: number | '') => (euros === '' ? undefined : Math.round(Number(euros) * 100));

// The simulation and the mutation take the same arguments, built from the form once. Whether the price is
// locked (no billing details to invoice) is the server's call: it bills 0 then, whatever is entered. No end
// date is ever sent: a new term is a year, an upgrade keeps the term's end — Odoo's subscription plan is
// yearly, and its next invoice date is moved by the billing date window alone.
const toVariables = (organisationId: string, values: FormValues): SuperadminPlanChangePreviewQueryVariables => {
  const isFlex = values.subscriptionType === SubscriptionType.Flex;
  const isCustom = values.subscriptionType === SubscriptionType.Custom;
  return {
    organisationId,
    subscriptionType: values.subscriptionType,
    effectiveAt: isFlex ? undefined : values.effectiveAt || undefined,
    chargeCents: values.overridePrice ? toCents(values.price) : undefined,
    force: values.force || undefined,
    customLimits: isCustom
      ? {
          monthlyCredits: values.monthlyCredits === '' ? undefined : Number(values.monthlyCredits),
          projectUnitPrice: toCents(values.projectUnitPrice),
          subscriptionPrice: toCents(values.subscriptionPrice),
        }
      : undefined,
  };
};

// What the server will do for the form as it stands. Debounced so typing a
// price or a date does not fire a query per keystroke; the last result stays
// on screen while the next loads, and `settled` says whether what is on screen
// is the answer for the form as it stands now — only then may it be confirmed.
// Always from the server: the simulation depends on today, so a cached one
// from an earlier day would prefill a date that has passed. A result for a
// previous plan is ignored.
const usePlanChangePreview = ({ organisationId, open }: { organisationId?: string; open?: boolean }): { preview?: Preview; settled: boolean } => {
  const { values } = useFormikContext<FormValues>();
  const variables = useMemo(() => toVariables(organisationId ?? '', values), [organisationId, values]);
  const debounced = useDebouncedValue(variables);
  const { data, previousData, loading } = useSuperadminPlanChangePreviewQuery({ variables: debounced, skip: !organisationId || !open, fetchPolicy: 'network-only' });
  const preview = data?.superadminPlanChangePreview ?? previousData?.superadminPlanChangePreview;
  const forThisPlan = preview?.subscriptionType === values.subscriptionType ? preview : undefined;
  return { preview: forThisPlan, settled: !!forThisPlan && !!data && !loading && debounced === variables };
};

// Fills in the server's defaults (today, the term's end, a custom deal's
// values) once per selected plan, so the inputs show what the simulation used.
const PrefillFromPreview: React.FC<{ preview?: Preview; open?: boolean }> = ({ preview, open }) => {
  const { setFieldValue } = useFormikContext<FormValues>();
  const prefilledFor = useRef<SubscriptionType | undefined>(undefined);
  useEffect(() => {
    if (!open) prefilledFor.current = undefined;
  }, [open]);
  useEffect(() => {
    if (!preview || preview.subscriptionType === prefilledFor.current) return;
    prefilledFor.current = preview.subscriptionType;
    // A downgrade has no dates: nothing to fill in, nothing to carry over to the next plan selected.
    const dated = preview.kind !== PlanChangeKind.ScheduledDowngrade && !!preview.endDate;
    setFieldValue('effectiveAt', dated ? toDateInput(preview.effectiveAt) : '');
    if (preview.subscriptionType === SubscriptionType.Custom) {
      setFieldValue('monthlyCredits', preview.after.monthlyCredits);
      setFieldValue('projectUnitPrice', preview.after.projectUnitPriceCents / 100);
      setFieldValue('subscriptionPrice', preview.after.yearlyPriceCents / 100);
    }
  }, [preview, setFieldValue]);
  return null;
};

const SubscriptionTypeSelect: React.FC = () => {
  const { setFieldValue } = useFormikContext<FormValues>();
  const planOptions = usePlanOptions();
  const translations = useTranslations();

  // Another plan starts from the server's defaults again, not from the dates entered for this one.
  const onChange: FormSelectProps['onChange'] = useCallback(() => {
    setFieldValue('effectiveAt', '');
    setFieldValue('overridePrice', false);
    setFieldValue('price', '');
    setFieldValue('force', false);
    setFieldValue('monthlyCredits', '');
    setFieldValue('projectUnitPrice', '');
    setFieldValue('subscriptionPrice', '');
  }, [setFieldValue]);

  return (
    <FormikSelect name="subscriptionType" label={translations.plan} placeholder={translations.plan} onChange={onChange}>
      {planOptions.map((option) => (
        <Option key={option.value} value={option.value}>
          {option.label}
        </Option>
      ))}
    </FormikSelect>
  );
};

// Standard plans have fixed allowances and prices; only CUSTOM is negotiated per org.
// A price of 0 is accepted and means free.
const CustomPlanFields: React.FC = () => {
  const { values } = useFormikContext<FormValues>();
  const translations = useTranslations();
  if (values.subscriptionType !== SubscriptionType.Custom) return null;
  return (
    <div className="grid grid-cols-3 gap-2">
      <FormikInput type="number" min={0} step={1} name="monthlyCredits" label={translations.monthlyCredits} required />
      <FormikInput type="number" min={0} step="0.01" name="projectUnitPrice" label={translations.projectUnitPrice} required />
      <FormikInput type="number" min={0} step="0.01" name="subscriptionPrice" label={translations.subscriptionPrice} required />
    </div>
  );
};

const Section: React.FC2<{ title: React.ReactNode; tone?: 'default' | 'highlight' }> = ({ title, tone = 'default', children, className }) => (
  <div className={classNames('flex flex-col gap-1 rounded-md border p-3', tone === 'highlight' ? 'border-neon-green-500 bg-neon-green-50/50 dark:bg-transparent' : 'border-gray-300 dark:border-gray-600', className)}>
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</span>
    {children}
  </div>
);

const Row: React.FC2<{ label: React.ReactNode; emphasis?: boolean }> = ({ label, emphasis, children, className }) => (
  <div className={classNames('flex items-baseline justify-between gap-4 text-sm', emphasis && 'font-semibold', className)}>
    <span className={classNames(!emphasis && 'text-gray-600 dark:text-gray-300')}>{label}</span>
    <span className="text-right">{children}</span>
  </div>
);

const Hint: React.FC2 = ({ children, className }) => <span className={classNames('text-xs text-gray-500', className)}>{children}</span>;

// "GROW · 8 projects/month · €1,490/year", or "Flex · pay as you go".
const PlanLine: React.FC<{ state: Pick<TermState, 'subscriptionType' | 'monthlyCredits' | 'yearlyPriceCents'> }> = ({ state }) => {
  const plans = useSubscriptionTypeTranslations();
  const translations = useTranslations();
  if (state.subscriptionType === SubscriptionType.Flex) return <span>{`${plans.FLEX} · ${translations.payAsYouGo}`}</span>;
  return (
    <>
      <span className="font-medium">{plans[state.subscriptionType]}</span> · <T _str="{credits} project(s)/month" args={{ credits: state.monthlyCredits }} /> · <T _str="{price}/year" args={{ price: formatPrice(state.yearlyPriceCents) }} />
    </>
  );
};

const TermRows: React.FC<{ state: TermState }> = ({ state }) => {
  const { formatDay } = useFormatDate();
  if (!state.startDate || !state.endDate) return null;
  return (
    <>
      <Row label={<T _str="term" swc />}>
        {formatDay(new Date(state.startDate))} → {formatDay(new Date(state.endDate))}
        {state.monthsTotal != null && (
          <Hint className="ml-1">
            (<T _str="{months} month(s)" args={{ months: state.monthsTotal }} />)
          </Hint>
        )}
      </Row>
      {state.active && state.monthsElapsed != null && state.monthsLeft != null && (
        <Row label={<T _str="months" swc />}>
          <T _str="{elapsed} over, {left} left" args={{ elapsed: state.monthsElapsed, left: state.monthsLeft }} />
          <Hint className="ml-1">
            (<T _str="the running month counts as left" />)
          </Hint>
        </Row>
      )}
      {state.active && state.nextRenewal && <Row label={<T _str="next renewal" swc />}>{formatDay(new Date(state.nextRenewal))}</Row>}
    </>
  );
};

const CurrentSection: React.FC<{ preview: Preview }> = ({ preview }) => {
  const plans = useSubscriptionTypeTranslations();
  const { formatDay } = useFormatDate();
  const { current } = preview;
  return (
    <Section title={<T _str="current subscription" swc />}>
      <Row label={<T _str="plan" swc />}>
        <PlanLine state={current} />
      </Row>
      <TermRows state={current} />
      {current.pausedAt && (
        <Row label={<T _str="status" swc />}>
          <span className="text-warning-yellow-700 dark:text-warning-yellow-500">
            <T _str="paused since {date}" args={{ date: formatDay(new Date(current.pausedAt)) }} />
          </span>
        </Row>
      )}
      {!current.active && current.startDate && new Date(current.startDate) > new Date() && (
        <Row label={<T _str="status" swc />}>
          <span className="text-warning-yellow-700 dark:text-warning-yellow-500">
            <T _str="starts {date}" args={{ date: formatDay(new Date(current.startDate)) }} />
          </span>
        </Row>
      )}
      {!current.active && current.endDate && new Date(current.endDate) <= new Date() && (
        <Row label={<T _str="status" swc />}>
          <span className="text-error-red-500">
            <T _str="ended {date}" args={{ date: formatDay(new Date(current.endDate)) }} />
          </span>
        </Row>
      )}
      {current.scheduledType && current.scheduledAt && (
        <Row label={<T _str="pending" swc />}>
          <span className="text-warning-yellow-700 dark:text-warning-yellow-500">
            <T _str="becomes {plan} on {date}" args={{ plan: plans[current.scheduledType], date: formatDay(dayAfterTermEnd(current.scheduledAt)) }} />
          </span>
        </Row>
      )}
      {preview.alreadyPaidCents > 0 && <Row label={<T _str="billed on this subscription" swc />}>{formatPrice(preview.alreadyPaidCents)}</Row>}
    </Section>
  );
};

// Only what the sections below cannot say: a pending change that confirming
// cancels or replaces.
const PendingChangeWarning: React.FC<{ preview: Preview }> = ({ preview }) => {
  const plans = useSubscriptionTypeTranslations();
  const { formatDay } = useFormatDate();
  const { scheduledType, scheduledAt } = preview.current;
  if (!scheduledType || !scheduledAt) return null;
  const pendingPlan = plans[scheduledType];
  const pendingDay = formatDay(dayAfterTermEnd(scheduledAt));
  if (preview.kind === PlanChangeKind.Unchanged) {
    return <InfoContent type="warning" title={<T _str="Change to {plan} scheduled for {date}" args={{ plan: pendingPlan, date: pendingDay }} swc />} description={<T _str="Confirming with the current plan selected cancels it." />} />;
  }
  if (preview.alreadyScheduled) return null;
  return (
    <InfoContent
      type="warning"
      title={<T _str="Change to {plan} scheduled for {date}" args={{ plan: pendingPlan, date: pendingDay }} swc />}
      description={
        preview.keepsPending && !preview.supersedesPending ? (
          <T _str="Confirming keeps it and adds this change after it." />
        ) : preview.keepsPending ? (
          <T _str="Confirming keeps the pending changes before this one's day and replaces the rest from that day on. What was sold for them stays counted as covered." />
        ) : (
          <T _str="Confirming replaces it from this change's day on. What was sold for it stays counted as covered." />
        )
      }
    />
  );
};

// Static keys, so the extraction finds them.
const useProblemMessages = (): Record<PlanChangeProblem, string> => {
  const effectiveDateInPast = useT('The effective date must be today or later.');
  const effectiveDateBeforeTerm = useT('The effective date must be on or after the day the term starts.');
  const endDateInPast = useT('The renewal date must be in the future.');
  const endDateBeforeStart = useT('The renewal date must be after the effective date.');
  const termTooLong = useT('The term may not be longer than 121 months.');
  const invalidCharge = useT('The price may not be negative.');
  const subscriptionPaused = useT('The subscription is paused: resume it first. Only cancelling is possible while it is paused.');
  const noTerm = useT('There is no running term.');
  const pendingChangeAfterEnd = useT('An upgrade is pending on or after that day.');
  const billingDetailsUnset = useT('Fill in the billing details first, or mark them as left blank on purpose (the plan is then free).');
  return useMemo(
    () => ({
      [PlanChangeProblem.EffectiveDateInPast]: effectiveDateInPast,
      [PlanChangeProblem.EffectiveDateBeforeTerm]: effectiveDateBeforeTerm,
      [PlanChangeProblem.EndDateInPast]: endDateInPast,
      [PlanChangeProblem.EndDateBeforeStart]: endDateBeforeStart,
      [PlanChangeProblem.TermTooLong]: termTooLong,
      [PlanChangeProblem.InvalidCharge]: invalidCharge,
      [PlanChangeProblem.SubscriptionPaused]: subscriptionPaused,
      [PlanChangeProblem.NoTerm]: noTerm,
      [PlanChangeProblem.PendingChangeAfterEnd]: pendingChangeAfterEnd,
      [PlanChangeProblem.BillingDetailsUnset]: billingDetailsUnset,
    }),
    [effectiveDateInPast, effectiveDateBeforeTerm, endDateInPast, endDateBeforeStart, termTooLong, invalidCharge, subscriptionPaused, noTerm, pendingChangeAfterEnd, billingDetailsUnset],
  );
};

const Problems: React.FC<{ preview: Preview }> = ({ preview }) => {
  const messages = useProblemMessages();
  if (!preview.problems.length) return null;
  return (
    <ul className="flex flex-col gap-1 text-sm text-error-red-500">
      {preview.problems.map((problem) => (
        <li key={problem}>{messages[problem]}</li>
      ))}
    </ul>
  );
};

// The one date the superadmin controls here: the day the change takes effect, where the change can be
// dated (an upgrade, a new term). A new term runs a year from it; an upgrade keeps the term's renewal
// date. A downgrade has no dates: it waits at the end of the term as it stands.
const DateFields: React.FC<{ preview: Preview }> = ({ preview }) => {
  const translations = useTranslations();
  const { formatDay } = useFormatDate();
  if (!preview.endDate || preview.subscriptionType === SubscriptionType.Flex || preview.kind === PlanChangeKind.ScheduledDowngrade) return null;
  if (preview.kind !== PlanChangeKind.Upgrade && preview.kind !== PlanChangeKind.NewSubscription) return null;
  const newTerm = preview.kind === PlanChangeKind.NewSubscription;
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="flex flex-col">
        <FormikDayInput name="effectiveAt" label={translations.effectiveAt} min={toDateInput(preview.minEffectiveAt)} max={preview.maxEffectiveAt ? toDateInput(preview.maxEffectiveAt) : undefined} required />
        <Hint>
          <T _str="Today or later. A later day schedules the change." />
        </Hint>
      </div>
      <div className="flex flex-col justify-end pb-6">
        <Hint>
          {newTerm ? (
            <T _str="A year from that day: the term renews on {date}. The billing date can be moved afterwards." args={{ date: formatDay(dayAfterTermEnd(preview.endDate)) }} />
          ) : (
            <T _str="Within the term. The renewal date stays {date}. To move it, change the billing date." args={{ date: formatDay(dayAfterTermEnd(preview.endDate)) }} />
          )}
        </Hint>
      </div>
    </div>
  );
};

// What the change does in Odoo, so the superadmin knows what sales will find there: the subscription order
// a new term makes, the upsell or draft line an upgrade adds, the recurring line a cheaper plan rewrites,
// the end date a cancellation sets — or the draft invoice the app makes where the subscription cannot carry
// the fee (no plan configured, no order linked yet).
const OdooSection: React.FC<{ preview: Preview; odooOrderLinked: boolean }> = ({ preview, odooOrderLinked }) => {
  const plans = useSubscriptionTypeTranslations();
  const { formatDay } = useFormatDate();
  const { kind } = preview;
  const isFlexTarget = preview.subscriptionType === SubscriptionType.Flex;
  const viaSubscription = preview.invoiceVia === InvoiceVia.OdooSubscription;
  const endsOn = preview.endDate ? formatDay(dayAfterTermEnd(preview.endDate)) : '';
  let text: React.ReactNode;
  if (kind === PlanChangeKind.NewSubscription && isFlexTarget) text = preview.voidsCurrentTerm && odooOrderLinked ? <T _str="The subscription's end date is set to today: Odoo closes it and invoices no further cycle." /> : <T _str="Nothing changes in Odoo." />;
  else if (kind === PlanChangeKind.NewSubscription)
    text = viaSubscription ? (
      preview.chargedCents > 0 ? (
        <T _str="A subscription order is made for the customer on the yearly plan, confirmed, with the first year as a draft invoice at the price above. Odoo invoices the renewals from then on." />
      ) : (
        <T _str="Priced at 0, no order is made yet: the subscription is made in Odoo by the first priced event (a renewal, an upgrade)." />
      )
    ) : (
      <T _str="A draft invoice is made for the fee. No subscription order: Odoo subscriptions are not configured, or the term is not a year." />
    );
  else if (kind === PlanChangeKind.Upgrade)
    text = !viaSubscription ? (
      <T _str="A draft invoice is made for the difference." />
    ) : !odooOrderLinked ? (
      <T _str="Not linked to an Odoo subscription yet. The difference is recorded here. Its upsell is made in Odoo once the term is linked. The daily job keeps trying." />
    ) : preview.chargedCents > 0 ? (
      <T _str="The difference goes on the term's invoice while it is still a draft. Once sent, it becomes an upsell on the subscription with its own draft invoice. The subscription's recurring line becomes {plan} at {price}/year for the next cycle." args={{ plan: plans[preview.subscriptionType], price: formatPrice(preview.after.yearlyPriceCents) }} />
    ) : (
      <T _str="Priced at 0, nothing is invoiced. The subscription's recurring line becomes {plan} at {price}/year for the next cycle." args={{ plan: plans[preview.subscriptionType], price: formatPrice(preview.after.yearlyPriceCents) }} />
    );
  else if (kind === PlanChangeKind.ScheduledDowngrade && isFlexTarget)
    text = odooOrderLinked ? <T _str="The subscription's end date is set to {date}: Odoo closes it then and invoices no further cycle. Sales can call it off there, which calls it off here too." args={{ date: endsOn }} /> : <T _str="Not linked to an Odoo subscription: nothing changes there." />;
  else if (kind === PlanChangeKind.ScheduledDowngrade)
    text = odooOrderLinked ? (
      <T _str="Nothing is invoiced now. The subscription's recurring line becomes {plan} at {price}/year, so Odoo invoices the cycle starting {date} at that price." args={{ plan: plans[preview.subscriptionType], price: formatPrice(preview.next?.yearlyPriceCents ?? 0), date: endsOn }} />
    ) : (
      <T _str="Not linked to an Odoo subscription: the year on {plan} is invoiced as a draft the day it starts." args={{ plan: plans[preview.subscriptionType] }} />
    );
  else if (kind === PlanChangeKind.Unchanged && preview.current.scheduledType)
    text = odooOrderLinked ? <T _str="The pending change is called off in Odoo too: the recurring line is the current plan again, and an end date is removed." /> : <T _str="Not linked to an Odoo subscription: nothing changes there." />;
  else if (kind === PlanChangeKind.Unchanged) text = preview.chargedCents > 0 ? <T _str="A draft invoice is made for the difference." /> : <T _str="Nothing changes in Odoo." />;
  else text = <T _str="Nothing changes in Odoo." />;
  return (
    <Section title={<T _str="in Odoo" swc />}>
      <Hint>{text}</Hint>
    </Section>
  );
};

const AfterSection: React.FC<{ preview: Preview }> = ({ preview }) => {
  const plans = useSubscriptionTypeTranslations();
  const { formatDay } = useFormatDate();
  const translations = useTranslations();
  const { after, kind } = preview;
  const effectiveAt = formatDay(dayAfterTermEnd(preview.effectiveAt));
  const grantedLater = !!preview.extraCreditsAt && new Date(preview.extraCreditsAt) > new Date();
  const isDowngrade = kind === PlanChangeKind.ScheduledDowngrade;
  return (
    <Section title={preview.scheduled && !isDowngrade ? <T _str="after the change on {date}" args={{ date: effectiveAt }} swc /> : <T _str="after confirmation" swc />} tone="highlight">
      <Row label={<T _str="plan" swc />}>
        <PlanLine state={after} />
      </Row>
      {after.startDate && after.endDate && (
        <Row label={<T _str="term" swc />}>
          {formatDay(new Date(after.startDate))} → {formatDay(new Date(after.endDate))}
          {after.monthsTotal != null && (
            <Hint className="ml-1">
              (<T _str="{months} month(s)" args={{ months: after.monthsTotal }} />)
            </Hint>
          )}
        </Row>
      )}
      {preview.timeline.length > 0 && (
        <div className="mt-1">
          <PlanCalendar
            timeline={preview.timeline}
            changeDay={preview.scheduled || kind === PlanChangeKind.Upgrade ? dayAfterTermEnd(preview.effectiveAt) : undefined}
            downgrade={kind === PlanChangeKind.ScheduledDowngrade ? { from: dayAfterTermEnd(preview.effectiveAt), to: preview.subscriptionType } : undefined}
          />
        </div>
      )}
      {isDowngrade && after.scheduledType && after.endDate && (
        <Row label={<T _str="from {date}" args={{ date: formatDay(dayAfterTermEnd(after.endDate)) }} swc />}>
          {preview.next && preview.next.subscriptionType !== SubscriptionType.Flex ? (
            <PlanLine state={preview.next} />
          ) : (
            <>
              <span className="font-medium">{plans[after.scheduledType]}</span>
              {` · ${translations.payAsYouGo}`}
            </>
          )}
        </Row>
      )}
      {preview.extraCredits > 0 && preview.extraCreditsAt && preview.extraCreditsExpireAt && (
        <Row label={grantedLater ? <T _str="projects granted on {date}" args={{ date: formatDay(new Date(preview.extraCreditsAt)) }} swc /> : <T _str="projects granted now" swc />}>
          <span className="font-medium text-neon-green-700 dark:text-neon-green-300">+{preview.extraCredits}</span>{' '}
          <Hint>
            <T _str="valid until {date}" args={{ date: formatDay(new Date(preview.extraCreditsExpireAt)) }} />
          </Hint>
        </Row>
      )}
      {isDowngrade && (
        <Hint>
          <T _str="Nothing changes until the end of the current term. Then the subscription continues on this plan instead of renewing." />
        </Hint>
      )}
    </Section>
  );
};

// The price, worked out in the open: the term's value after the change, minus
// what the current term is already worth, is the difference; an override
// replaces it. What is billed is written to the history as a subscription fee.
const PriceSection: React.FC<{ preview: Preview }> = ({ preview }) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const plans = useSubscriptionTypeTranslations();
  const translations = useTranslations();
  const { formatDay } = useFormatDate();
  const { overridePrice, price } = values;
  const priceLocked = preview.priceLocked;

  // Ticking the box starts from the computed amount — once, so the field can then be emptied to type another.
  const wasOverriding = useRef(false);
  useEffect(() => {
    if (overridePrice && !wasOverriding.current && price === '') setFieldValue('price', preview.dueCents / 100);
    wasOverriding.current = overridePrice;
  }, [overridePrice, price, preview.dueCents, setFieldValue]);

  if (!preview.endDate && preview.lines.length === 0) return null;
  const isFlexTarget = preview.subscriptionType === SubscriptionType.Flex;
  const recordsFee = preview.chargedCents > 0 || (!isFlexTarget && preview.kind !== PlanChangeKind.Unchanged);
  // A downgrade to FLEX only ever bills the current plan (for a longer term); every other fee is the new plan's.
  const feePlan = isFlexTarget ? preview.current.subscriptionType : preview.subscriptionType;
  const billedLater = preview.scheduled && !isFlexTarget;
  const inCustomersFavour = preview.balanceCents < 0;

  if (preview.kind === PlanChangeKind.ScheduledDowngrade) {
    const startsOn = formatDay(dayAfterTermEnd(preview.effectiveAt));
    return (
      <Section title={<T _str="price" swc />}>
        <Row label={<T _str="billed now" swc />} emphasis className="text-base">
          {formatPrice(0)}
        </Row>
        <Hint>
          {isFlexTarget ? (
            <T _str="Nothing is billed. The current term runs as it stands. On {date} the subscription ends." args={{ date: startsOn }} />
          ) : (
            <T _str="Nothing is billed now. The current term runs as it stands. On {date} the subscription continues on {plan} for a year, billed then at {price}/year." args={{ date: startsOn, plan: plans[preview.subscriptionType], price: formatPrice(preview.next?.yearlyPriceCents ?? 0) }} />
          )}
        </Hint>
      </Section>
    );
  }

  return (
    <Section title={<T _str="price" swc />}>
      {preview.lines.map((line) => (
        <Row key={`${line.subscriptionType}-${line.from}`} label={`${plans[line.subscriptionType]} · ${line.months} × ${formatPrice(line.monthlyPriceCents)}`}>
          <Hint className="mr-2">
            {formatDay(new Date(line.from))} → {formatDay(new Date(line.to))}
          </Hint>
          {formatPrice(line.totalCents)}
        </Row>
      ))}
      <Row label={<T _str="value of the term after the change" swc />} emphasis>
        {formatPrice(preview.totalValueCents)}
      </Row>
      {preview.currentTermValueCents > 0 && (
        <Row label={preview.keepsPending || preview.supersedesPending ? <T _str="already covered by the current term and its pending changes" swc /> : <T _str="already covered by the current term" swc />}>
          − {formatPrice(preview.currentTermValueCents)}
        </Row>
      )}
      <Row label={<T _str="difference" swc />} emphasis className="border-t border-gray-300 pt-1 dark:border-gray-600">
        {formatPrice(preview.dueCents)}
      </Row>
      {inCustomersFavour && (
        <Hint>
          <T _str="The term after the change is worth {balance} less than what was already covered. That balance stays in the customer's favour. Nothing is refunded automatically." args={{ balance: formatPrice(-preview.balanceCents) }} />
        </Hint>
      )}
      {priceLocked ? (
        // Nobody to invoice: the change goes ahead, at no price.
        <div className="mt-2 grid grid-cols-2 items-end gap-2">
          <NoBillingDetailsWarning />
          <FormInput name="lockedPrice" label={translations.price} value={formatPrice(0)} disabled readOnly hideErrorLabel />
        </div>
      ) : (
        <div className="mt-2 grid grid-cols-2 items-end gap-2">
          <FormikCheckBox name="overridePrice" label={translations.overridePrice} hideErrorLabel className="pb-3" />
          {overridePrice ? (
            <FormikInput type="number" min={0} step="0.01" name="price" label={translations.price} required hideErrorLabel />
          ) : (
            <FormInput name="computedPrice" label={translations.price} value={formatPrice(preview.dueCents)} disabled readOnly hideErrorLabel />
          )}
        </div>
      )}
      <Row label={billedLater ? <T _str="billed on {date}" args={{ date: formatDay(dayAfterTermEnd(preview.effectiveAt)) }} swc /> : <T _str="billed now" swc />} emphasis className="text-base">
        {formatPrice(preview.chargedCents)}
      </Row>
      <Hint>
        {!recordsFee && <T _str="Nothing is billed or recorded." />}
        {recordsFee && billedLater && <T _str="Recorded in the history now as a {plan} subscription fee dated {date}." args={{ plan: plans[feePlan], date: formatDay(dayAfterTermEnd(preview.effectiveAt)) }} />}
        {recordsFee && !billedLater && <T _str="Recorded in the history as a {plan} subscription fee." args={{ plan: plans[feePlan] }} />}
      </Hint>
      {preview.after.endDate && !isFlexTarget && (
        <Row label={<T _str="price at renewal on {date}" args={{ date: formatDay(dayAfterTermEnd(preview.after.endDate)) }} swc />} className="mt-2 border-t border-gray-300 pt-2 dark:border-gray-600">
          <T _str="{price}/year" args={{ price: formatPrice(preview.after.yearlyPriceCents) }} />
          <Hint className="ml-1">
            (<T _str="{credits} project(s)/month" args={{ credits: preview.after.monthlyCredits }} />)
          </Hint>
        </Row>
      )}
    </Section>
  );
};

// The one way to cut a term short: only offered where the change would
// otherwise wait for the end of the term, and kept while it is ticked.
const ForceField: React.FC<{ preview: Preview }> = ({ preview }) => {
  const { values } = useFormikContext<FormValues>();
  const translations = useTranslations();
  if (!values.force && preview.kind !== PlanChangeKind.ScheduledDowngrade) return null;
  return (
    <div className="flex flex-col gap-2">
      <FormikCheckBox name="force" label={translations.force} hideErrorLabel />
      {preview.voidsCurrentTerm && (
        <InfoContent
          type="warning-red"
          title={<T _str="The current term is voided now" swc />}
          description={<T _str="Its monthly projects are removed right away and nothing of it counts as paid. Its pending change, if any, is dropped." />}
        />
      )}
    </div>
  );
};

// onReadyChange: whether the form may be confirmed as it stands — the simulation is in, current, and finds no problem.
const Fields: React.FC<{ organisationId?: string; open?: boolean; odooOrderLinked: boolean; onReadyChange: (ready: boolean) => void }> = ({ organisationId, open, odooOrderLinked, onReadyChange }) => {
  const { preview, settled } = usePlanChangePreview({ organisationId, open });
  const ready = settled && !!preview && preview.problems.length === 0;
  useEffect(() => onReadyChange(ready), [onReadyChange, ready]);
  return (
    <div className="flex flex-col gap-3">
      <PrefillFromPreview preview={preview} open={open} />
      {preview && <CurrentSection preview={preview} />}
      <SubscriptionTypeSelect />
      {preview && preview.kind === PlanChangeKind.Unchanged && !preview.endDate && (
        <Hint>
          <T _str="Pay as you go: no term, no monthly projects. Pick a plan to see what a subscription would change." />
        </Hint>
      )}
      {preview && (
        <>
          <PendingChangeWarning preview={preview} />
          <DateFields preview={preview} />
          <CustomPlanFields />
          <ForceField preview={preview} />
          <Problems preview={preview} />
          <AfterSection preview={preview} />
          <PriceSection preview={preview} />
          <OdooSection preview={preview} odooOrderLinked={odooOrderLinked} />
        </>
      )}
    </div>
  );
};

export interface ChangePlanModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId?: string;
  // The running term is linked to a subscription order in Odoo (see OdooSection).
  odooOrderLinked?: boolean;
  initialValues?: {
    monthlyCredits: number;
    projectUnitPrice: number;
    subscriptionPrice: number;
    subscriptionType: SubscriptionType;
  };
}
const ChangePlanModal_: React.FC2<ChangePlanModalProps> = ({ onClose, open, organisationId, odooOrderLinked = false, initialValues }) => {
  // The mutation returns the organisation as it stands (the card's fragment): the cache updates the card in
  // place, nothing is refetched — refetching every active query reloaded the whole organisations list.
  const [updatePlan, { loading: updatePlanLoading }] = useSuperadminUpdatePlanMutation();
  const translations = useTranslations();
  const { showToast } = useToastNotifications();
  const [ready, setReady] = useState(false);

  // Only a CUSTOM plan stores its own values; the simulation suggests the rest.
  const initialFormValues: FormValues = useMemo(() => {
    const custom = initialValues?.subscriptionType === SubscriptionType.Custom;
    return {
      subscriptionType: initialValues?.subscriptionType ?? SubscriptionType.Flex,
      monthlyCredits: custom ? initialValues.monthlyCredits : '',
      projectUnitPrice: custom ? initialValues.projectUnitPrice : '',
      subscriptionPrice: custom ? initialValues.subscriptionPrice : '',
      effectiveAt: '',
      overridePrice: false,
      price: '',
      force: false,
    };
  }, [initialValues]);

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async (values) => {
      if (!organisationId) return;
      const variables = toVariables(organisationId, values);
      try {
        await updatePlan({ variables: { ...variables, customLimits: variables.customLimits ?? {} } });
        // The window unmounts its form as it closes: nothing to reset.
        onClose();
      } catch (e) {
        showToast({ title: translations.failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      }
    },
    [onClose, organisationId, showToast, translations.failed, updatePlan],
  );

  const formik: FormikConfig<FormValues> = useMemo(() => ({ initialValues: initialFormValues, onSubmit }), [onSubmit, initialFormValues]);

  return (
    <CreateModal
      title={translations.changePlan}
      createButtonTitle={translations.confirm}
      formik={formik}
      onClose={onClose}
      open={open}
      isSubmitting={updatePlanLoading}
      submitDisabled={!ready}
      panelClassName="max-w-2xl"
      modalBodyClassName="min-h-[26rem] max-h-[70vh] overflow-y-auto px-1 pr-3 py-1"
      showCloseIcon
    >
      <Fields organisationId={organisationId} open={open} odooOrderLinked={odooOrderLinked} onReadyChange={setReady} />
    </CreateModal>
  );
};

export const ChangePlanModal = memo(ChangePlanModal_);
